import * as AccountDomain from "./account-domain"
import * as ObservationDomain from "./observation-domain"
import * as LoanDomain from "./loan-domain"
import * as ModelDomain from "./model-domain"
import * as ResultDomain from "./result-domain"
import * as ScenarioDomain from "./scenario-domain"
import * as ProspectiveDomain from "./prospective-domain"
import * as Model from "../../model"
import { findAccountAmount } from "./prospective-domain"
import * as Tabledefinition from "./../../component/scenarii/detail/table-definition"
import Operations from "./../../component/scenarii/common/operations"

/**
 * @deprecated
 * @see AccountDomain.EXPENSES_ACCOUNT_NUMBERS
 */
export const GROSS_CASH_FLOW_ACCOUNTS_TO_ADD = ["675", "676", "68"]
/**
 * @deprecated
 * @see AccountDomain.REVENUES_ACCOUNT_NUMBERS
 */
export const GROSS_CASH_FLOW_ACCOUNTS_TO_SUBSTRACT = ["775", "776", "777", "78"]
/**
 * @deprecated
 */
export const NET_CASH_FLOW_ACCOUNTS_TO_SUBTRACT = ["164"]

/**
 * Calcule le nombre années de désendettement de la capacité d'autofinancement,
 * soit le résultat de `capital restant dû / capacité d'autofinancement brute`.
 * @param grossCashFlow
 * @param remainingCapital
 */
export function calculateCashFlowDeleveragingYears(
	grossCashFlow: NonNullable<number>,
	remainingCapital: NonNullable<number>
): number | null
/**
 * Calcule pour plusieurs années, le nombre années de désendettement de la
 * capacité d'autofinancement, soit le résultat de
 * `capital restant dû / capacité d'autofinancement brute`.
 * @param grossCashFlows
 * @param remainingCapitals
 */
export function calculateCashFlowDeleveragingYears(
	grossCashFlows: number[],
	remainingCapitals: number[]
): number[]
export function calculateCashFlowDeleveragingYears(
	grossCashFlows: number | number[],
	remainingCapitals: number | number[]
): number | number[] | null {
	if ("number" === typeof grossCashFlows && "number" === typeof remainingCapitals)
		return grossCashFlows ? remainingCapitals / grossCashFlows : null

	if (Array.isArray(grossCashFlows) && Array.isArray(remainingCapitals))
		return grossCashFlows.map((gcf, gcfIndex) =>
			calculateCashFlowDeleveragingYears(gcf || 0, remainingCapitals[gcfIndex] || 0))
}

/**
 * Calcule le pourcentage de remboursement de la dette sur la CAF brute, soit le
 * résultat de `remboursement capital dette / capacité d'autofinancement brute`.
 * @param grossCashFlow
 * @param reimbursedCapital
 */
export function calculateDebtRefundPercentage(
	grossCashFlow: NonNullable<number>,
	reimbursedCapital: NonNullable<number>
): number | null
/**
 * Calcule pour plusieurs années, le pourcentage de remboursement de la dette
 * sur la CAF brute, soit le résultat de
 * `remboursement capital dette / capacité d'autofinancement brute`.
 * @param grossCashFlows
 * @param reimbursedCapitals
 */
export function calculateDebtRefundPercentage(
	grossCashFlows: number[],
	reimbursedCapitals: number[]
): number[]
export function calculateDebtRefundPercentage(
	grossCashFlows: number | number[],
	reimbursedCapitals: number | number[]
): number | number[] | null {
	if ("number" === typeof grossCashFlows && "number" === typeof reimbursedCapitals)
		return grossCashFlows ? reimbursedCapitals / grossCashFlows : null

	if (Array.isArray(grossCashFlows) && Array.isArray(reimbursedCapitals))
		return grossCashFlows.map((gcf, gcfIndex) =>
			calculateDebtRefundPercentage(gcf || 0, reimbursedCapitals[gcfIndex] || 0))
}

/**
 * Calule la Capacité d'Autofinancement Brute à partir du résultat net
 * `netResult`, des charges `expenses` et des produits `revenues`, soit le
 * résultat de `résultat net + charges - produits`.
 * @param netResult
 * @param expenses
 * @param revenues
 */
export function calculateGrossCashFlow(
	netResult: number,
	expenses: number,
	revenues: number
): number
/**
 * Calule pour plusieurs années, la Capacité d'Autofinancement Brute à partir du
 * résultat net `netResults`, des charges `expenses` et des produits `revenues`,
 * soit pour chaque année le résultat de `résultat net + charges - produits`.
 * @param netResults
 * @param expenses
 * @param revenues
 */
export function calculateGrossCashFlow(
	netResults: number[],
	expenses: number[],
	revenues: number[]
): number[]
/**
 * Calule pour l'année `year`, la Capacité d'Autofinancement Brute
 * rétrospective.
 * @param localAuthority
 * @param year
 */
export function calculateGrossCashFlow(
	localAuthority: Model.LocalAuthority,
	year: number
): number
/**
 * Calule pour l'année `year`, la Capacité d'Autofinancement Brute en
 * s'appuyant sur l'année du scénario pour déterminer si elle est rétrospective
 * ou prospective.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param year
 */
export function calculateGrossCashFlow(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	year: number
): number
/**
 * Calule pour les années `years`, la Capacité d'Autofinancement Brute en
 * s'appuyant sur l'année du scénario pour déterminer si elle est rétrospective
 * ou prospective.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 */
export function calculateGrossCashFlow(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[]
): number[]
export function calculateGrossCashFlow(
	a: number | number[] | Model.LocalAuthority,
	b: number | number[] | Model.Scenario,
	c?: number | number[] | Model.Ppi,
	d?: Model.LoanDecomposition[],
	e?: number | number[]
): number | number[] {
	if ("number" === typeof a && "number" === typeof b && "number" === typeof c)
		return a + b - c

	if (Array.isArray(a) && Array.isArray(b) && Array.isArray(c))
		return (a as number[]).map((r, i) => calculateGrossCashFlow(r, b[i], c[i]))

	if (ModelDomain.isLocalAuthority(a) && "number" === typeof b) {
		const localAuthority = a as Model.LocalAuthority
		const year = b as number
		const grossCashFlowObs = findGrossCashFlowObservations(
			localAuthority.observations, year)

		return grossCashFlowObs.reduce((result, o) =>
			o.receipts ? result + o.amount : result - o.amount, 0)
	}

	const localAuthority = a as Model.LocalAuthority
	const scenario = b as Model.Scenario
	const ppi = c as Model.Ppi
	const loanDecompositions = d as Model.LoanDecomposition[]

	if ("number" === typeof e) {
		const year = e as number

		if (!scenario || !ppi || scenario.year > year) {
			const grossCashFlowObs = findGrossCashFlowObservations(
				localAuthority.observations, year)
			return grossCashFlowObs.reduce((result, o) =>
			o.receipts ? result + o.amount : result - o.amount, 0)
		}

		return localAuthority.accounts.reduce((grossCashFlow, laca) => {
			if (!AccountDomain.isGrossCashFlowAccount(laca.account))
				return grossCashFlow

			if (laca.account.receipts)
				grossCashFlow += findAccountAmount(
					localAuthority,
					laca.account,
					laca.chapter,
					scenario,
					ppi,
					loanDecompositions,
					true,
					year
				)

			if (laca.account.expense)
				grossCashFlow -= findAccountAmount(
					localAuthority,
					laca.account,
					laca.chapter,
					scenario,
					ppi,
					loanDecompositions,
					false,
					year
				)

			return grossCashFlow
		}, 0)
	}

	const years = e as number[]

	return years.map(year =>
		calculateGrossCashFlow(
			localAuthority, scenario, ppi, loanDecompositions, year))
}

/**
 * Calcule la capacité d'autofinancement nette, soit le résultat de
 * `CAF brute - remboursement des emprunts`.
 * @param grossCashFlow
 * @param loansReimbursement
 */
export function calculateNetCashFlow(
	grossCashFlow: NonNullable<number>,
	loansReimbursement: NonNullable<number>
): NonNullable<number>
/**
 * Calcule pour plusieurs années, les capacités d'autofinancement nettes, soit
 * le résultat de `CAF brute - remboursement des emprunts`.
 * @param grossCashFlows
 * @param loansReimbursements
 */
export function calculateNetCashFlow(
	grossCashFlows: number[],
	loansReimbursements: number[]
): NonNullable<number>[]
/**
 * Calcule pour l'année `year`la capacité d'autofinancement nette rétrospective.
 * @param localAuthority
 * @param loanDecompositions
 * @param year
 *
 * @see calculateGrossCashFlow
 * @see LoanDomain.getTotalYearReimbursedCapital
 */
export function calculateNetCashFlow(
	localAuthority: Model.LocalAuthority,
	loanDecompositions: Model.LoanDecomposition[],
	year: number
): number
/**
 * Calule pour l'année `year`, la Capacité d'Autofinancement nette en
 * s'appuyant sur l'année du scénario pour déterminer si elle est rétrospective
 * ou prospective.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param year
 *
 * @see calculateGrossCashFlow
 * @see LoanDomain.getTotalYearReimbursedCapital
 */
export function calculateNetCashFlow(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	year: number
): number[]
/**
 * Calule pour les années `years`, la Capacité d'Autofinancement nette en
 * s'appuyant sur l'année du scénario pour déterminer si elle est rétrospective
 * ou prospective.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 *
 * @see calculateGrossCashFlow
 * @see LoanDomain.getTotalYearReimbursedCapital
 */
export function calculateNetCashFlow(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[]
): number[]
export function calculateNetCashFlow(
	a: NonNullable<number> | number[] | Model.LocalAuthority,
	b: NonNullable<number> | number[] | Model.LoanDecomposition[] | Model.Scenario,
	c?: number | Model.Ppi,
	d?: Model.LoanDecomposition[],
	e?: number | number[]
): number | number[] {
	if ("number" === typeof a && "number" === typeof b) return a - b

	if (2 === arguments.length && Array.isArray(a) && Array.isArray(b)) {
		const grossCashFlows = a as number[]
		const loansReimbursements = b as number[]
		return grossCashFlows.map((gcf, gcfIndex) => calculateNetCashFlow(
			gcf || 0, loansReimbursements[gcfIndex] || 0))
	}

	if (3 === arguments.length
		&& ModelDomain.isLocalAuthority(a)
		&& Array.isArray(b)
		&& "number" === typeof c) {
		const localAuthority = a as Model.LocalAuthority
		const loanDecompositions = b as Model.LoanDecomposition[]
		const year = c
		const grossCashFlow = calculateGrossCashFlow(localAuthority, year)
		const reimbursedCapital = LoanDomain.getTotalYearReimbursedCapital(
			loanDecompositions, year)

		return calculateNetCashFlow(grossCashFlow, reimbursedCapital)
	}

	const localAuthority = a as Model.LocalAuthority
	const scenario = b as Model.Scenario
	const ppi = c as Model.Ppi
	const loanDecompositions = d as Model.LoanDecomposition[]

	if ("number" === typeof e) {
		const year = e as number
		const grossCashFlow = calculateGrossCashFlow(
			localAuthority, scenario, ppi, loanDecompositions, year)
		const reimbursedCapital = LoanDomain.getTotalYearReimbursedCapital(
			loanDecompositions, year)

		return calculateNetCashFlow(grossCashFlow, reimbursedCapital)
	}

	const years = e as number[]
	const grossCashFlows = calculateGrossCashFlow(
		localAuthority, scenario, ppi, loanDecompositions, years)
	const reimbursedCapitals = years.map(y =>
		LoanDomain.getTotalYearReimbursedCapital(loanDecompositions, y))

	return calculateNetCashFlow(grossCashFlows, reimbursedCapitals)
}

/**
 * Calcule pour les années `year`, les dépenses réelles de fonctionnement en
 * s'appuyant sur l'année du scénario pour déterminer si elles sont
 * rétrospectives ou prospectives.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 */
export function calculateRealOperationExpense(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[]
): number[] {
	return calculateRealOperationBudget(
		localAuthority, scenario, ppi, loanDecompositions, years, false)
}

/**
 * Calcule pour les années `year`, les recettes réelles de fonctionnement en
 * s'appuyant sur l'année du scénario pour déterminer si elles sont
 * rétrospectives ou prospectives.
 * @param localAuthority
 * @param scenario
 * @param ppi
 * @param loanDecompositions
 * @param years
 */
export function calculateRealOperationReceipts(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[]
): number[] {
	return calculateRealOperationBudget(
		localAuthority, scenario, ppi, loanDecompositions, years, true)
}

/**
 * Détermine le nombre d'années de désendettement de la CAF en fonction des
 * paramètres passés.
 * @param observations
 * @param decompositions
 * @param year
 */
export function determineCashFlowDeleveragingYears(
	observations: Model.Observation[],
	decompositions: Model.LoanDecomposition[],
	year: number
): number | null {
	const remainingCapital = LoanDomain.getTotalYearRemainingCapital(
		decompositions, year)
	const grossCashFlow = getGrossCashFlowAmount(observations, year)
	return calculateCashFlowDeleveragingYears(grossCashFlow, remainingCapital)
}

/**
 * Détermine le pourcentage de remboursement de la dette sur la CAF en fonction
 * des paramètres passés.
 * @param observations
 * @param decompositions
 * @param year
 */
export function determineDebtRefundPercentage(
	observations: Model.Observation[],
	decompositions: Model.LoanDecomposition[],
	year: number
): number | null {
	const reimbursedCapital = LoanDomain.getTotalYearReimbursedCapital(
		decompositions, year)
	const grossCashFlow = getGrossCashFlowAmount(observations, year)
	return calculateDebtRefundPercentage(grossCashFlow, reimbursedCapital)
}

/**
 * Met sous forme de chaîne de caractères les années de désendettement.
 * @param cashFlowDeleveragingYears
 */
export function formatDeleveragingYears(
	cashFlowDeleveragingYears: number
): string {
	const absoluteDeleveragingYears = Math.abs(cashFlowDeleveragingYears)

	let years = Math.floor(absoluteDeleveragingYears)
	let months = Math.round((absoluteDeleveragingYears - years) * 12)

	if (12 === months) {
		months = 0
		years += 1
	}

	let result = years ? (years <= 1 ? `${years} an` : `${years} ans`) : ""

	if (years && months) result += ", "
	if (months) result += `${months} mois`

	return cashFlowDeleveragingYears < 0 ? `-${result}` : result
}

/**
 * @deprecated
 * @see findGrossCashFlowAccounts
 *
 * Retourne la liste des comptes à additionner et à soustraire pour calculer la
 * CAF brute.
 * @param lacas
 */
export function getGrossCashFlowAccounts(
	lacas: Model.LocalAuthorityChapterAccount[]
): GrossCashFlowAccounts {
	return lacas.reduce<GrossCashFlowAccounts>((gcfAccounts, laca) => {
		if (isGrossCashFlowAccountToAdd(laca.account))
			gcfAccounts.toAdd.push(laca.account)
		else if (isGrossCashFlowAccountToSubtract(laca.account))
			gcfAccounts.toSubtract.push(laca.account)

		return gcfAccounts
	}, { toAdd: [], toSubtract: [] })
}

/**
 * @deprecated
 * @see calculateGrossCashFlow
 *
 * Retourne la CAF brute pour l'année spécifiée en paramètre à partir de la
 * liste d'observations spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getGrossCashFlowAmount(
	observations: Model.Observation[],
	year: number
): number {
	let amount = ResultDomain.getCalculatedOperationResultAmount(observations, year)
	const grossCashFlowObservations = getGrossCashFlowObservations(observations, year)

	grossCashFlowObservations.toAdd.forEach(observation => {
		amount += observation.amount
	})

	grossCashFlowObservations.toSubtract.forEach(observation => {
		amount -= observation.amount
	})

	return amount
}

/**
 * @deprecated
 * @see calculateGrossCashFlow
 *
 * Retourne la liste des CAF brutes pour les années spécifiées à partir de la
 * liste d'observations spécifiée en paramètre.
 * @param observations
 * @param years
 */
export function getGrossCashFlowAmounts(
	observations: Model.Observation[],
	years: number[]
): number[] {
	return years.map(year => (
		getGrossCashFlowAmount(observations, year)
	))
}

/**
 * Retourne la liste des constats d'opération de la CAF brute de l'année
 * spécifiée en paramètre à partir de la liste d'observations spécifiée en
 * paramètre.
 * @param observations
 * @param year
 */
export function getGrossCashFlowObservations(
	observations: Model.Observation[],
	year: number
): GrossCashFlowObservations {
	const summableObservations: GrossCashFlowObservations = {
		toAdd: [],
		toSubtract: []
	}

	observations.forEach(observation => {
		if (year !== observation.year) return

		if (isGrossCashFlowAccountToAdd(observation.account))
			summableObservations.toAdd.push(observation)
		else if (isGrossCashFlowAccountToSubtract(observation.account))
			summableObservations.toSubtract.push(observation)
	})

	return summableObservations
}

/**
 * Retourne la liste des comptes à soustratire pour calculer la CAF nette.
 * @param lacas
 */
export function getNetCashFlowAccountsToSubtract(
	lacas: Model.LocalAuthorityChapterAccount[]
): Model.Account[] {
	return lacas.reduce<Model.Account[]>((accounts, laca) => {
		if (isNetCashFlowAccountToSubtract(laca.account))
			accounts.push(laca.account)

		return accounts
	}, [])
}

/**
 * @deprecated
 * @see calculateNetCashFlow
 *
 * Retourne la CAF nette pour l'année spécifiée en paramètre à partir de la
 * liste d'observations spécifiée en paramètre.
 * @param observations
 * @param year
 */
export function getNetCashFlowAmount(
	observations: Model.Observation[],
	year: number
): number {
	let amount = getGrossCashFlowAmount(observations, year)

	ObservationDomain
	.getLoanRepaymentObservations(observations, year)
	.forEach(observation => {
		amount -= observation.amount
	})

	return amount
}

/**
 * Retourne la liste des CAF nettes pour les années spécifiées à partir de la
 * liste d'observations spécifiée en paramètre.
 * @param observations
 * @param years
 */
export function getNetCashFlowAmounts(
	observations: Model.Observation[],
	years       : number[]
): number[] {
	return years.map(year => (
		getNetCashFlowAmount(observations, year)
	))
}

/**
 * Liste les constats de comptes utilisés pour calculer la capacité
 * d'autofinancement brute, soit les constats de comptes qui ne sont ni des
 * charges, ni des produits.
 * @param observations
 * @see AccountDomain.isExpenseAccount
 * @see AccountDomain.isRevenueAccount
 */
export function findGrossCashFlowObservations(
	observations: Model.Observation[]
): Model.Observation[]
/**
 * Liste pour l'année `year`, les constats de comptes utilisés pour calculer la
 * capacité d'autofinancement brute, soit les constats de comptes qui ne sont ni
 * des charges, ni des produits.
 * @param observations
 * @param year
 * @see AccountDomain.isExpenseAccount
 * @see AccountDomain.isRevenueAccount
 */
export function findGrossCashFlowObservations(
	observations: Model.Observation[],
	year: number
): Model.Observation[]
/**
 * Liste pour les années `years`, les constats de comptes utilisés pour calculer
 * la capacité d'autofinancement brute, soit les constats de comptes qui ne sont
 * ni des charges, ni des produits.
 * @param observations
 * @param years
 * @see AccountDomain.isExpenseAccount
 * @see AccountDomain.isRevenueAccount
 */
export function findGrossCashFlowObservations(
	observations: Model.Observation[],
	years: number[]
): Model.Observation[][]
export function findGrossCashFlowObservations(
	observations: Model.Observation[],
	y?: number | number[]
): Model.Observation[] | Model.Observation[][] {
	if (!y) return observations.filter(o => {
		return o.account
		&& o.account.operation
		&& !AccountDomain.isExpenseAccount(o.account)
		&& !AccountDomain.isRevenueAccount(o.account)
	})

	if ("number" === typeof y) return observations.filter(o => {
		return o.account
		&& o.account.operation
		&& y === o.year
		&& !AccountDomain.isExpenseAccount(o.account)
		&& !AccountDomain.isRevenueAccount(o.account)
	})

	return observations.reduce<Model.Observation[][]>((result, o) => {
		if (!(o.account && o.account.operation)) return result

		y.forEach((year, yearIndex) => {
			if (year === o.year
				&& !AccountDomain.isExpenseAccount(o.account)
				&& !AccountDomain.isRevenueAccount(o.account))
				result[yearIndex].push(o)
		})

		return result
	}, y.map(() => []))
}

/**
 * Retourne le pourcentage de Caf Brute en fonction du RRF 
 * pour une année donnée
 * @param observations
 * @param year
 */
export function getCafBruteforaYear(
	CAFBrute:number,
	RRF:number
): number {	
	return (RRF==0?0:CAFBrute/RRF);
}

/**
 * Retourne la liste des epargnes courantes 
 * pour les années données
 * @param observations
 * @param years
 */
export function getEpargneCouranteforYears(
	la:Model.LocalAuthority,
	accounts: Model.LocalAuthorityChapterAccount[],
	years       : number[],
	scenario: Model.Scenario,
	ppi: Model.Ppi, 
	loans: Model.LoanDecomposition[],
	amountFinder?: AccountAmountFinder
): number[] {
	return years.map(year => (
		getEpargneCouranteforaYear(la,accounts, year, scenario, ppi, loans, amountFinder)
	))
}

/**
 * Retourne l'epargne courante
 * pour une année donnée
 * @param observations
 * @param year
 */
export function getEpargneCouranteforaYear(
	la:Model.LocalAuthority,
	accounts: Model.LocalAuthorityChapterAccount[],
	year       : number,
	scenario: Model.Scenario,
	ppi: Model.Ppi, 
	loans: Model.LoanDecomposition[],
	amountFinder?: AccountAmountFinder
): number {	
	let acc013:number =  getChapterAmountForYear(la,accounts,year,"013", scenario, ppi, loans, amountFinder);
	let acc70:number =  getChapterAmountForYear(la,accounts,year,"70", scenario, ppi, loans, amountFinder);
	let acc73:number =  getChapterAmountForYear(la,accounts,year,"73", scenario, ppi, loans, amountFinder);
	let acc731:number =  getChapterAmountForYear(la,accounts,year,"731", scenario, ppi, loans, amountFinder);
	let acc74:number =  getChapterAmountForYear(la,accounts,year,"74", scenario, ppi, loans, amountFinder);
	let acc75:number =  getChapterAmountForYear(la,accounts,year,"75", scenario, ppi, loans, amountFinder);
	let acc011:number = getChapterAmountForYear(la,accounts,year,"011", scenario, ppi, loans, amountFinder);
	let acc012:number =  getChapterAmountForYear(la,accounts,year,"012", scenario, ppi, loans, amountFinder);
	let acc65:number =  getChapterAmountForYear(la,accounts,year,"65", scenario, ppi, loans, amountFinder);
	return acc013+acc70+acc73+acc731+acc74+acc75-acc011-acc012-acc65;
}

/**
 * Retourne la liste des epargnes de gestion 
 * pour les années données
 * @param observations
 * @param years
 */
export function getEpargneGestionforYears(
	la:Model.LocalAuthority,
	accounts: Model.LocalAuthorityChapterAccount[],
	years       : number[],
	scenario: Model.Scenario,
	ppi: Model.Ppi, 
	loans: Model.LoanDecomposition[],
	amountFinder?: AccountAmountFinder
): number[] {
	return years.map(year => (
		getEpargneGestionforaYear(la,accounts, year, scenario, ppi, loans, amountFinder)
	))
}

/**
 * Retourne l'epargne de gestion
 * pour une année donnée
 * @param observations
 * @param year
 */
export function getEpargneGestionforaYear(
	la:Model.LocalAuthority,
	accounts: Model.LocalAuthorityChapterAccount[],
	year       : number,
	scenario: Model.Scenario,
	ppi: Model.Ppi, 
	loans: Model.LoanDecomposition[],
	amountFinder?: AccountAmountFinder
): number {
	let acc77:number = getChapterAmountForYear(la,accounts,year,"77", scenario, ppi, loans, amountFinder);
	let acc78:number = getChapterAmountForYear(la,accounts,year,"78", scenario, ppi, loans, amountFinder);
	let acc67:number = getChapterAmountForYear(la,accounts,year,"67", scenario, ppi, loans, amountFinder);
	let acc68:number = getChapterAmountForYear(la,accounts,year,"68", scenario, ppi, loans, amountFinder);
	return acc77+acc78-acc67-acc68;
}

/**
 * Retourne la liste des epargnes courantes 
 * pour les années données
 * @param observations
 * @param years
 */
export function getEpargneGestionCouranteforYears(
	la:Model.LocalAuthority,
	accounts: Model.LocalAuthorityChapterAccount[],
	years       : number[],
	scenario: Model.Scenario,
	ppi: Model.Ppi, 
	loans: Model.LoanDecomposition[],
	amountFinder?: AccountAmountFinder
): number[] {
	return years.map(year => (
		getEpargneGestionCouranteforaYear(la,accounts, year, scenario, ppi, loans, amountFinder)
	))
}

/**
 * Retourne l'epargne courante
 * pour une année donnée
 * @param observations
 * @param year
 */
export function getEpargneGestionCouranteforaYear(
	la:Model.LocalAuthority,
	accounts: Model.LocalAuthorityChapterAccount[],
	year       : number,
	scenario: Model.Scenario,
	ppi: Model.Ppi, 
	loans: Model.LoanDecomposition[],
	amountFinder?: AccountAmountFinder
): number {	
	let acc013:number =  getChapterAmountForYear(la,accounts,year,"013", scenario, ppi, loans, amountFinder);
	let acc70:number =  getChapterAmountForYear(la,accounts,year,"70", scenario, ppi, loans, amountFinder);
	let acc73:number =  getChapterAmountForYear(la,accounts,year,"73", scenario, ppi, loans, amountFinder);
	let acc731:number =  getChapterAmountForYear(la,accounts,year,"731", scenario, ppi, loans, amountFinder);
	let acc74:number =  getChapterAmountForYear(la,accounts,year,"74", scenario, ppi, loans, amountFinder);
	let acc75:number =  getChapterAmountForYear(la,accounts,year,"75", scenario, ppi, loans, amountFinder);
	let acc011:number = getChapterAmountForYear(la,accounts,year,"011", scenario, ppi, loans, amountFinder);
	let acc012:number =  getChapterAmountForYear(la,accounts,year,"012", scenario, ppi, loans, amountFinder);
	let acc014:number =  getChapterAmountForYear(la,accounts,year,"014", scenario, ppi, loans, amountFinder);
	let acc65:number =  getChapterAmountForYear(la,accounts,year,"65", scenario, ppi, loans, amountFinder);
	return acc013+acc70+acc73+acc731+acc74+acc75-acc011-acc012-acc014-acc65;
}

/** Chapitres de dépenses utilisés dans le calcul des épargnes. */
const EPARGNE_EXPENSE_CHAPTERS = ["011", "012", "014", "65", "67", "68"]

/**
 * Calcule le montant d'un compte pour une année, dans le sens donné. Permet au
 * rapport de gestion de reprendre le moteur de la Synthèse du scénario.
 */
export type AccountAmountFinder = (
	laca: Model.LocalAuthorityChapterAccount,
	receipts: boolean,
	year: number
) => number

function getChapterAmountForYear(la:Model.LocalAuthority,accounts:Model.LocalAuthorityChapterAccount[],year:number,chapterNumber:string, scenario: Model.Scenario,
	ppi: Model.Ppi, loans: Model.LoanDecomposition[], amountFinder?: AccountAmountFinder
):number{
	let chapterAmount:number=0;
	let chapterAccounts = accounts.filter(acc=>acc.chapter!=null && acc.chapter.number == chapterNumber);
	if(chapterAccounts){
		chapterAccounts.forEach(acc=>{
			if (amountFinder) {
				// Sens déduit du chapitre (et non du compte, qui peut être mixte)
				const receipts = EPARGNE_EXPENSE_CHAPTERS.indexOf(chapterNumber) === -1
				if (!acc.account.operation) return
				if ((receipts && !acc.account.receipts) || (!receipts && !acc.account.expense)) return
				chapterAmount += amountFinder(acc, receipts, year) || 0;
				return
			}
			chapterAmount += ProspectiveDomain.findAccountAmount(la, acc.account, acc.chapter, scenario, ppi, loans, acc.account.receipts, year);
		});
	}

	return chapterAmount;
}

/**
 * @deprecated
 * @see isExpenseAccount
 *
 * Vérifie si le compte passé en paramètre est à additionner pour calculer la
 * CAF brute.
 * @param account
 */
export function isGrossCashFlowAccountToAdd(account: Model.Account): boolean {
	if (!account.operation) return false

	for (const number of GROSS_CASH_FLOW_ACCOUNTS_TO_ADD)
		if (account.number.startsWith(number)) return true

	return false
}

/**
 * @deprecated
 * @see isRevenueAccount
 *
 * Vérifie si le compte passé en paramètre est à soustraire pour calculer la
 * CAF brute.
 * @param account
 */
export function isGrossCashFlowAccountToSubtract(account: Model.Account): boolean {
	if (!account.operation) return false

	for (const number of GROSS_CASH_FLOW_ACCOUNTS_TO_SUBSTRACT)
		if (account.number.startsWith(number)) return true

	return false
}

/**
 * @deprecated
 * @see AccountDomain.isLoanReimbursement
 *
 * Vérifie si le compte passé en paramètre est à soustraire pour calculer la
 * CAF nette.
 * @param account
 */
export function isNetCashFlowAccountToSubtract(account: Model.Account): boolean {
	if (account.operation) return false
	if (account.receipts) return false
	if (!account.expense) return false

	for (const number of NET_CASH_FLOW_ACCOUNTS_TO_SUBTRACT)
		if (account.number.startsWith(number)) return true

	return false
}

export interface GrossCashFlowAccounts {
	toAdd: Model.Account[]
	toSubtract: Model.Account[]
}

export interface GrossCashFlowObservations {
	toAdd: Model.Observation[]
	toSubtract: Model.Observation[]
}

function calculateRealOperationBudget(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	years: number[],
	receipts: boolean
): number[] {
	return years.map(year => {
		if (scenario && year >= scenario.year) {
			return receipts
			? ScenarioDomain.calculateRealOperationReceipts(
				localAuthority, scenario, ppi, loanDecompositions, year)
			: ScenarioDomain.calculateRealOperationExpense(
				localAuthority, scenario, ppi, loanDecompositions, year)
		}

		return receipts
		? ObservationDomain.calculateRealOperationReceipts(localAuthority, year)
		: ObservationDomain.calculateRealOperationExpense(localAuthority, year)
	})
}



// WEBPACK FOOTER //
// ./src/domain/sfp/cash-flow-domain.ts